import { Link } from "react-router-dom";
import { useEffect } from "react";

import { useTranslation } from "@/core/i18n";
import { cn } from "@/lib/utils";
import { useHidePricing, useIsAnonymousBrowsing } from "@/modules/anonymousBrowsing";
import type { ListingTypeOptions } from "@/utils/productTypes";
import { Models } from "api/types";
import { Icon } from "atoms/icon/Icon";
import ChevronRight from "atoms/icon/icons/chevron_right_small.svg";
import { ImagePreloader } from "atoms/image-preloader/ImagePreloader";
import { DescriptiveTag } from "atoms/tag/DescriptiveTag";
import { JustForYouTag } from "atoms/tag/JustForYouTag";
import { useIntersectionObserver } from "hooks/useIntersectionObserver";
import { usePrefetchPDP } from "hooks/usePrefetchPDP";
import { usePriceDisplayState } from "hooks/usePriceDisplayState";
import { useFeatureFlag } from "services/Flags";
import { getPricingText } from "services/ListingHelperService";
import { formatCurrency } from "toolbox/Money";
import { formatBillingPeriodInterval, parseBillingPeriodName } from "utils/billingPeriod.helpers";

type ProductCardLargeProps = {
  title: string;
  description?: string | null;
  imageUrl?: string | null;
  type: ListingTypeOptions;
  tag?: string | null;
  concerns?: Array<string> | null;
  price: number;
  memberPrice?: number | null;
  onClick?: () => void;
  /** A real navigation link for public catalog cards. */
  to?: string;
  unitType?: string | null | undefined;
  hasLinearPricing?: boolean | undefined;
  discount?: string | null | undefined;
  discountData?: Models<"ShopProductDiscountModel"> | null;
  customColor?: string | null;
  guestMode?: boolean;
  priceRange?: string | null;
  isRecommended?: boolean;
  /** Optional: ID of the item to enable prefetching */
  itemId?: string;
  bookingEnabled?: boolean;
  billingPeriodInterval?: number | null;
  billingPeriodIntervalCount?: number | null;
  billingPeriodCost?: number | null;
  /** A quieter badge after `tag`, e.g. a retail product's "Best seller". */
  secondaryTag?: string | null;
  /** How the item reaches the patient, shown under the title, e.g. "Shipped by SkinDrop". */
  fulfillmentLabel?: string | null;
  /**
   * `contain` letterboxes the image on white instead of cropping it to the frame: retail packshots
   * are shot on white, and a crop cuts into the product.
   */
  imageFit?: "cover" | "contain";
};

export const ProductCardLarge = ({
  title,
  description,
  imageUrl,
  type,
  tag,
  price,
  memberPrice,
  unitType,
  hasLinearPricing,
  discount,
  discountData,
  customColor,
  guestMode,
  priceRange,
  isRecommended,
  itemId,
  bookingEnabled,
  billingPeriodInterval,
  billingPeriodIntervalCount,
  billingPeriodCost,
  secondaryTag,
  fulfillmentLabel,
  imageFit = "cover",
  onClick,
  to,
}: ProductCardLargeProps) => {
  const { t } = useTranslation();
  const isAnonymousBrowsing = useIsAnonymousBrowsing();
  // `guestMode` says the viewer has no account; whether that hides prices depends on
  // the platform's anonymous-checkout setting, so never gate price display on it directly.
  const hidePricing = useHidePricing({ isGuest: !!guestMode });
  const isFlexibleMembershipsEnabled = useFeatureFlag("flexible_membership_terms");
  const [ref, isIntersecting] = useIntersectionObserver<HTMLDivElement>({
    rootMargin: "200px",
    threshold: 0,
    triggerOnce: true,
  });

  const { prefetchPackagePDP, prefetchMembershipPDP, prefetchProductPDP } = usePrefetchPDP();

  const getCardPriceText = () => {
    if (type === "Membership" && isFlexibleMembershipsEnabled && billingPeriodInterval != null) {
      const parsedInterval = parseBillingPeriodName(billingPeriodInterval);
      return formatBillingPeriodInterval(
        billingPeriodCost ?? price,
        parsedInterval,
        billingPeriodIntervalCount ?? 1,
      );
    }

    return getPricingText(hasLinearPricing, price, unitType, type);
  };

  useEffect(() => {
    if (!isIntersecting || !itemId) return;

    switch (type) {
      case "Treatment":
      case "Package":
        prefetchPackagePDP(itemId);
        break;
      case "Membership":
        prefetchMembershipPDP(itemId);
        break;
      case "Product":
      case "ProductFromExternalSource":
        prefetchProductPDP(itemId);
        break;
    }
  }, [isIntersecting, itemId, type, prefetchPackagePDP, prefetchMembershipPDP, prefetchProductPDP]);

  const shouldShowBookNow = bookingEnabled && price == 0;

  const displayPrice = getCardPriceText();

  // Use shared hook for price display state calculation
  const { discountedPrice, hasStrikethroughPrice, hasMultiplePriceOptions, shouldShowMemberPrice } =
    usePriceDisplayState({
      basePriceInCents: price,
      memberPriceInCents: memberPrice,
      discountData,
      isGuest: hidePricing,
      hasPriceRange: !!priceRange,
      hasLinearPricing,
      type,
    });

  return (
    <div
      ref={ref}
      className="group relative flex min-h-[140px] cursor-pointer flex-col rounded-xl bg-white p-3 focus-within:outline focus-within:outline-2 focus-within:outline-offset-4 focus-within:outline-brand-color"
      onClick={to ? undefined : onClick}
    >
      <div className="flex gap-3 desk:flex-col desk:gap-0">
        <div className="relative h-32 w-24 shrink-0 overflow-hidden rounded-xl desk:aspect-[4/5] desk:h-auto desk:w-full">
          {imageUrl ? (
            <ImagePreloader
              url={imageUrl}
              lazy={true}
              fade={150}
              preloadDistance="100px"
              className="h-full w-full"
              placeholder={<div className="h-full w-full animate-pulse bg-gray-100" />}
              errorPlaceholder={
                <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-2">
                  <span className="text-center text-[10px] leading-tight text-gray-400">
                    {t("productCardLarge.imageComingSoon")}
                  </span>
                </div>
              }
            >
              <img
                className={cn(
                  "h-full w-full motion-safe:transition-transform motion-safe:duration-300 motion-safe:desk:group-hover:scale-[1.025]",
                  imageFit === "contain" ? "bg-white object-contain" : "object-cover",
                )}
                src={imageUrl}
                alt={title}
              />
            </ImagePreloader>
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-2">
              <span className="text-center text-[10px] leading-tight text-gray-400">
                {t("productCardLarge.imageComingSoon")}
              </span>
            </div>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col desk:pt-3">
          {(isRecommended || discount || tag || secondaryTag) && (
            <div className="mb-1 flex flex-wrap items-center gap-1">
              {isRecommended && <JustForYouTag />}
              {discount && (
                <DescriptiveTag
                  color={customColor ? "custom" : "primary"}
                  size="small"
                  customColor={customColor}
                >
                  {discount}
                </DescriptiveTag>
              )}
              {tag && (
                <DescriptiveTag color="primary" size="small">
                  {tag}
                </DescriptiveTag>
              )}
              {secondaryTag && (
                <DescriptiveTag color="secondary" size="small">
                  {secondaryTag}
                </DescriptiveTag>
              )}
            </div>
          )}
          <h3 className="mb-1 line-clamp-2 font-serif text-[22px] leading-[1.2] tracking-tight text-primary">
            {to ? (
              <Link
                to={to}
                className="after:absolute after:inset-0 after:rounded-xl focus-visible:outline-none"
              >
                {title}
              </Link>
            ) : (
              title
            )}
          </h3>
          {fulfillmentLabel && (
            <p className="mb-1 text-body3 font-semibold uppercase tracking-wide text-secondary">
              {fulfillmentLabel}
            </p>
          )}
          {description && (
            <p className="line-clamp-3 break-words pb-1 pr-1 text-body2 leading-snug text-secondary desk:line-clamp-2">
              {description}
            </p>
          )}
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between gap-2 border-t border-outline-grey pt-3 desk:mt-0 desk:border-0 desk:pt-2">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {hidePricing && (
            <span className="text-body2 text-primary">
              {isAnonymousBrowsing
                ? t("anonymousBrowsing.logInForPricing")
                : t("productCardLarge.signUpToSeePrice")}
            </span>
          )}
          {!hidePricing &&
            (shouldShowBookNow ? (
              <span className="text-body2 font-semibold text-primary">
                {t("productCardLarge.bookNow")}
              </span>
            ) : (
              <>
                {hasStrikethroughPrice && !priceRange && discountedPrice != null ? (
                  <span className="text-body2 font-semibold text-primary">
                    {hasMultiplePriceOptions ? t("productCardLarge.fromPrefix") : ""}
                    {formatCurrency(discountedPrice)}{" "}
                    <span className="font-normal text-secondary line-through">
                      {formatCurrency(price)}
                    </span>
                  </span>
                ) : (
                  <span
                    className={`text-body2 text-primary ${
                      shouldShowMemberPrice ? "" : "font-semibold"
                    }`}
                  >
                    {priceRange || displayPrice}
                  </span>
                )}
                {shouldShowMemberPrice && memberPrice != null && (
                  <>
                    <span className="text-body2 text-neutral-300">|</span>
                    <span className="text-body2 font-semibold text-primary">
                      {formatCurrency(memberPrice)}{" "}
                      <span className="font-normal">{t("productCardLarge.member")}</span>
                    </span>
                  </>
                )}
              </>
            ))}
        </div>
        <Icon svg={ChevronRight} size="xsmall" color="brandColor" />
      </div>
    </div>
  );
};
